// ─────────────────────────────────────────────────────────────
// Helpers used by the Mapping UI.
// Pure, no React imports — easy to unit-test.
// ─────────────────────────────────────────────────────────────

import type {
  LinkDef, ItemDef, CodeList, OID, Trigger, TriggerValue, ConditionClause, Task, MappingTaskAction,
} from './types';
import { RESERVED_FORMULA_TOKENS } from './constants';

/** Task action types that resolve to an automation plan — the only actions a Switch-Plan link
 *  can target. */
export const AUTOMATION_PLAN_ACTION_TYPES = new Set(['Start Automation Plan', 'End Automation Plan']);

/** Reads a task's actions array out of the opaque `_raw` escape hatch (the mapping Task type does
 *  not model `actions` — everything not explicitly modeled is preserved verbatim in `_raw`). */
export function getTaskActions(task: Task): MappingTaskAction[] {
  const raw = task._raw as { actions?: unknown } | undefined;
  return Array.isArray(raw?.actions) ? (raw!.actions as MappingTaskAction[]) : [];
}

/** {index, action} pairs for this task's Start/End Automation Plan actions, in array order. */
export function planActionEntries(task: Task): Array<{ index: number; action: MappingTaskAction }> {
  return getTaskActions(task)
    .map((action, index) => ({ index, action }))
    .filter(({ action }) => AUTOMATION_PLAN_ACTION_TYPES.has(action.action ?? ''));
}

/** The subset of `planActionEntries` a Switch-Plan link may actually be authored against: actions
 *  whose "Resolve plan from this task's own MetaKey" opt-in is already switched ON in the Trade
 *  Template Builder's Actions accordion (`TaskInspector`).
 *
 *  That opt-in is what the tenant-side reconciler reads to decide whether to rewrite an action's
 *  automationPlanMasterUid from the answer, so a link authored against an action WITHOUT it would
 *  publish cleanly and then silently never fire. Gating the pickers on it makes the Builder toggle
 *  the single explicit opt-in and keeps un-opted-in tasks out of the Mapping tab entirely. */
export function resolvablePlanActionEntries(task: Task): Array<{ index: number; action: MappingTaskAction }> {
  return planActionEntries(task).filter(({ action }) => action.resolvePlanFromOwnMetaKey === true);
}

/** Filter links by source question and/or target task. */
export function linksFor(
  links: LinkDef[],
  filter: { itemOID?: OID; taskId?: number } = {},
): LinkDef[] {
  return links.filter(l =>
    (!filter.itemOID || l.sourceItemOID === filter.itemOID) &&
    (!filter.taskId  || l.targetTaskIds.includes(filter.taskId)),
  );
}

/** Generate the backend-facing runtime expression for a link's trigger. */
export function expressionFor(link: LinkDef): string {
  const oid = link.sourceItemOID ?? '';
  if (link.mode === 'populate') {
    return link.populate?.expression ?? `parseInt(${oid})||0`;
  }
  if (link.mode === 'remove') {
    const op = link.trigger?.operator ?? 'eq';
    const vals = link.trigger?.values ?? [];
    if (op === 'eq' && vals.length === 1) {
      const v = vals[0];
      if (typeof v === 'boolean') return `!(${oid} == ${v ? 'true' : 'false'})`;
      return `!(${oid} == "${v}")`;
    }
    if (op === 'in' && vals.length) {
      return '!(' + vals.map(v => `${oid} == "${v}"`).join(' || ') + ')';
    }
    if (op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte') {
      const sym = ({ gt: '>', gte: '>=', lt: '<', lte: '<=' } as const)[op];
      return `!(parseInt(${oid})||0 ${sym} ${vals[0] ?? 0})`;
    }
  }
  return '/* unsupported */';
}

/** Render a trigger as plain English using the question label and decode labels. */
export function readableTrigger(
  link: LinkDef,
  codeList: CodeList | null | undefined,
  itemLabel: string | undefined,
): string {
  const t = link.trigger ?? ({} as Trigger);
  const vals: TriggerValue[] = t.values ?? [];
  const qName = itemLabel ? `"${itemLabel}"` : 'answer';

  if (t.operator === 'always') return 'Always';
  if (vals.length === 0) return '—';

  const labels = vals.map(v => {
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (codeList) {
      const ci = codeList.CodeListItem.find(c => c.CodedValue === v);
      if (ci) return `"${ci.Decode.TranslatedText.text}"`;
    }
    return String(v);
  });

  switch (t.operator) {
    case 'eq':           return `When ${qName} is ${labels.join(' or ')}`;
    case 'ne':           return `When ${qName} is not ${labels.join(', ')}`;
    case 'in':           return `When ${qName} is ${labels.join(' or ')}`;
    case 'gt':           return `When ${qName} is greater than ${labels[0]}`;
    case 'gte':          return `When ${qName} is ${labels[0]} or more`;
    case 'lt':           return `When ${qName} is less than ${labels[0]}`;
    case 'lte':          return `When ${qName} is ${labels[0]} or less`;
    case 'not_contains': return `When ${qName} does not include ${labels.join(', ')}`;
    case 'contains':     return `When ${qName} includes ${labels.join(', ')}`;
    case 'is_empty':     return `When ${qName} is empty`;
    default:             return `When ${qName} ${labels.join(', ')}`;
  }
}

/** Turn a JS expression into plain English by stripping coercion + swapping OIDs for question text. */
export function humanExpression(
  expr: string | undefined,
  itemByOid: Record<OID, ItemDef>,
): string {
  if (!expr) return '';
  let h = expr;
  // Strip JS coercion wrappers — show the bare reference
  h = h.replace(/parseInt\(\s*(I\.\d+)\s*\)\s*\|\|\s*0/g, '$1');
  h = h.replace(/parseFloat\(\s*(I\.\d+)\s*\)\s*\|\|\s*0/g, '$1');
  h = h.replace(/\(\s*(I\.\d+)\s*\)/g, '$1');
  h = h.replace(/String\(\s*(I\.\d+)\s*\|\|\s*""\s*\)/g, '$1');
  // Friendly operators
  h = h.replace(/\s*\*\s*/g, ' × ');
  h = h.replace(/\s*\/\s*/g, ' ÷ ');
  // Replace OIDs with quoted question text
  h = h.replace(/I\.(\d+)/g, (m, n: string) => {
    const it = itemByOid['I.' + n];
    return it ? `"${it.Question.TranslatedText.text}"` : m;
  });
  // Collapse stray double-parens
  h = h.replace(/\(\(("[^"]+")\)\)/g, '$1');
  return h.trim();
}

/** Detect free variables in a formula template (single letters or words). */
export function detectFormulaVars(template: string | undefined): string[] {
  if (!template) return [];
  const tokens = template.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) ?? [];
  // Deduplicate without Set spread — ES5 compatible
  const filtered = tokens.filter(t => !RESERVED_FORMULA_TOKENS.has(t));
  return filtered.filter((t, i) => filtered.indexOf(t) === i);
}

/** Build the runtime expression for a formula by substituting OIDs for variables. */
export function buildFormulaExpression(
  template: string,
  vars: Record<string, OID | null>,
): string {
  if (!template) return '';
  let expr = template;
  // Sort longest-first to avoid partial replacement (e.g. "ab" before "a")
  const keys = Object.keys(vars).sort((a, b) => b.length - a.length);
  keys.forEach(v => {
    const oid = vars[v];
    if (!oid) return;
    // Whole-token replacement only. A naive split/join on a single-letter var (e.g. "a")
    // also rewrites the letters inside an already-injected "parseFloat(...)" token, corrupting
    // the expression when vars are substituted in non-alphabetical order. The word-boundary
    // regex matches the variable only as a standalone identifier.
    const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    expr = expr.replace(new RegExp(`\\b${escaped}\\b`, 'g'), `(parseFloat(${oid})||0)`);
  });
  return expr;
}

/** Render a formula as plain English using question labels. */
export function humanFormula(
  template: string,
  vars: Record<string, OID | null>,
  itemByOid: Record<OID, ItemDef>,
): string {
  if (!template) return '';
  let h = template;
  const keys = Object.keys(vars).sort((a, b) => b.length - a.length);
  keys.forEach(v => {
    const oid = vars[v];
    const item = oid ? itemByOid[oid] : undefined;
    if (!item) return;
    const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    h = h.replace(new RegExp(`\\b${escaped}\\b`, 'g'), `"${item.Question.TranslatedText.text}"`);
  });
  return h;
}

/** Build a plain-English description of a condition clause. */
export function humanCondition(
  clause: ConditionClause,
  itemByOid: Record<OID, ItemDef>,
  codeByOid: Record<OID, CodeList>,
): string {
  const item = itemByOid[clause.field];
  const qn = item ? `"${item.Question.TranslatedText.text}"` : clause.field;
  const cl = item?.CodeListRef ? codeByOid[item.CodeListRef.CodeListOID] : undefined;
  const labels = clause.values.map(v => {
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (cl) {
      const ci = cl.CodeListItem.find(c => c.CodedValue === v);
      if (ci) return `"${ci.Decode.TranslatedText.text}"`;
    }
    return String(v);
  });
  const opMap: Record<string, string> = {
    eq: 'is', ne: 'is not',
    gt: '>', gte: '≥', lt: '<', lte: '≤',
    contains: 'includes', not_contains: 'excludes',
  };
  const op = opMap[clause.op] ?? clause.op;
  return `${qn} ${op} ${labels.join(' or ')}`;
}
